class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = this.querySelector('#shopping-cart-line-item-status');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.itemsContainer = this.querySelector('#main-cart-items-notification');
    if (this.itemsContainer) {
      this.itemsContainer.addEventListener('change', this.debouncedOnChange.bind(this));
    }

    // this.addEventListener('change', this.debouncedOnChange.bind(this));

    this.html = document.documentElement;
    this.notificationWrapper = document.getElementById('cart-notification-wrapper');
    this.notification = document.getElementById('cart-notification');
    this.mobileAddCartButton = document.getElementById('mobile-add-cart-button');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    
    this.notification.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
    });
    this.querySelectorAll('.cart-notification__close').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    if (this.notification.classList.contains('active')) return;
    this.activeElement ||= document.activeElement;
    this.locksScroll = !this.html.classList.contains('no-scroll');
    this.html.classList.add('no-scroll');
    this.notification.inert = false;
    this.notification.removeAttribute('aria-hidden');
    this.notificationWrapper.classList.add('active');
    this.notification.classList.add('animate', 'active');
    this.stickyWasHidden = this.mobileAddCartButton?.classList.contains('hidden');
    this.mobileAddCartButton?.classList.add('hidden');
    this.restoreBackground = inertOutside(this.notificationWrapper);
    trapFocus(this.notification, this.querySelector('#CartDrawerTitle'));
    document.body.addEventListener('click', this.onBodyClick);
  }

  close(returnFocus = true) {
    if (!this.notification.classList.contains('active')) return;
    if (this.locksScroll) this.html.classList.remove('no-scroll');
    this.notificationWrapper.classList.remove('active');
    this.notification.classList.remove('active');
    if (!this.stickyWasHidden) this.mobileAddCartButton?.classList.remove('hidden');
    document.body.removeEventListener('click', this.onBodyClick);
    CartAccessibility.stopCheckoutWatch?.();
    this.restoreBackground?.();
    this.restoreBackground = null;
    const opener = this.activeElement?.isConnected ? this.activeElement : document.getElementById('cart-icon-bubble');
    removeTrapFocus(returnFocus ? opener : null);
    this.notification.inert = true;
    this.notification.setAttribute('aria-hidden', 'true');
  }

  disconnectedCallback() {
    this.close(false);
    document.body.removeEventListener('click', this.onBodyClick);
  }

  renderContents(parsedState, options = {}) {
    this.productId = parsedState.id;
    const saved = options.focus || CartAccessibility.capture(this);
    const wasOpen = this.notification.classList.contains('active');
    this.renderSections(parsedState.sections);
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, input) => total + Number(input.value), 0);
    this.updateEmptyState(this.currentItemCount);
    if (wasOpen) CartAccessibility.restore(this.notification, saved, this.querySelector('#CartDrawerTitle'));
    if (!options.fromUpsell && !wasOpen) {
      this.header?.reveal();
      this.open();
    }
    CartAccessibility.announce(this, this.currentItemCount, options.product);
  }

  updateEmptyState(itemCount) {
    this.classList.toggle('is-empty', itemCount === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', itemCount === 0);
    document.getElementById('cart-extra-products')?.classList.toggle('hide-extra-products', itemCount === 0);
  }

  renderSections(sections) {
    // Validate every fragment before changing the drawer so a partial response cannot leave mixed totals/items.
    const updates = this.getSectionsToRender().map((section) => {
      const container = document.getElementById(section.id);
      const target = container?.querySelector(section.selector) || container;
      const html = sections?.[section.section || section.id];
      if (!target || typeof html !== 'string') throw new Error(window.cartStrings.error);
      return { target, html: this.getSectionInnerHTML(html, section.selector) };
    });
    updates.forEach(({ target, html }) => { target.innerHTML = html; });
  }

  getSectionIds() {
    return this.getSectionsToRender().map((section) => section.section || section.id);
  }

  async refresh() {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('sections', this.getSectionIds().join(','));
    const response = await fetch(url);
    if (!response.ok) throw new Error(window.cartStrings.error);
    this.renderContents({ sections: await response.json() });
  }

  onChange(event) {
    if (event.target.matches('[name="updates[]"]')) this.updateQuantity(event.target.dataset.index, event.target.value);
  }

  getSectionsToRender() {
    return [
      // {
      //   id: 'cart-notification-product',
      //   selector: `#cart-notification-product-${this.productId}`,
      // },
      {
        id: 'main-cart-items-notification',
        section: this.itemsContainer.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-notification-progress',
        section: 'cart-notification-progress',
        selector: '#cart-notification-progress-inner',
      },
      {
        id: 'cart-notification-upsells',
        section: 'cart-notification-upsells',
        selector: '#cart-upsell-row-container'
      },
      {
        id: 'cart-notification-checkout-button',
        section: 'cart-notification-checkout-button',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
      }
    ];
  }

  async updateQuantity(line, quantity) {
    if (this.quantityUpdating || this.upsellAdding) return;
    this.quantityUpdating = true;
    const saved = CartAccessibility.capture(this);
    const input = this.querySelector(`#Quantity-${line}`);
    const originalQuantity = input?.defaultValue;
    const error = this.querySelector('#cart-errors');
    if (error) error.textContent = '';
    let confirmed = false;
    let rejected = false;
    this.enableLoading(line);
    try {
      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ line, quantity: Number(quantity), sections: this.getSectionIds(), sections_url: window.location.pathname }),
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        rejected = true;
        throw new Error(state.description || state.message || window.cartStrings.error);
      }
      confirmed = true;
      this.renderSections(state.sections);
      this.updateEmptyState(state.item_count);
      this.currentItemCount = state.item_count;
      this.disableLoading();
      CartAccessibility.restore(this.notification, saved, this.querySelector('#CartDrawerTitle'));
      CartAccessibility.announce(this, state.item_count);
      const actual = this.querySelector(`#Quantity-${line}`);
      if (Number(quantity) > 0 && actual && Number(actual.value) !== Number(quantity)) {
        error.textContent = window.cartStrings.quantityError.replace('[quantity]', actual.value);
      }
    } catch (failure) {
      if (confirmed || !rejected) {
        // An uncertain write must be reconciled by loading the real cart, never retried automatically.
        window.location.assign(document.getElementById('cart-icon-bubble').href);
      } else {
        if (input) input.value = originalQuantity;
        if (error) error.textContent = failure.message || window.cartStrings.error;
      }
    } finally {
      this.querySelectorAll('.loading-overlay').forEach(overlay => overlay.classList.add('hidden'));
      this.disableLoading();
      this.quantityUpdating = false;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!section) throw new Error(window.cartStrings.error);
    return section.innerHTML;
  }

  enableLoading() {
    this.itemsContainer.setAttribute('aria-busy', 'true');
    this.lineItemStatusElement?.setAttribute('aria-hidden', 'false');
  }

  disableLoading() {
    this.itemsContainer.removeAttribute('aria-busy');
    this.lineItemStatusElement?.setAttribute('aria-hidden', 'true');
  }

  handleBodyClick(event) {
    if (event.target === this.notificationWrapper) this.close();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);



document.addEventListener('DOMContentLoaded', function () {

  const cartBubble = document.getElementById('cart-icon-bubble');
  const cartNotification = document.querySelector('cart-notification');
  
  if (cartBubble && cartNotification) {
    cartBubble.setAttribute('role', 'button');
    cartBubble.setAttribute('aria-haspopup', 'dialog');
    cartBubble.setAttribute('aria-controls', 'cart-notification');
    cartBubble.addEventListener('keydown', event => {
      if (event.key === ' ') { event.preventDefault(); cartBubble.click(); }
    });
    cartBubble.addEventListener('click', async function(event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (cartBubble.getAttribute('aria-busy') === 'true') return;
      cartBubble.setAttribute('aria-busy', 'true');
      cartNotification.setActiveElement(cartBubble);
      try {
        await cartNotification.refresh();
      } catch (error) {
        window.location.assign(cartBubble.href);
      } finally {
        cartBubble.removeAttribute('aria-busy');
      }
    });
  }

  // --- Cart Upsell ---
  document.getElementById('cart-notification-upsells')?.addEventListener('submit', async function (event) {
    const form = event.target.closest('form.cart-upsell-form');
    const btn = form?.querySelector('.add-upsell-to-cart');
    if (!btn) return;
    event.preventDefault();
    if (!form || !cartNotification || cartNotification.upsellAdding || cartNotification.quantityUpdating) return;
    const focus = CartAccessibility.capture(cartNotification);
    cartNotification.upsellAdding = true;
    const originalText = btn.textContent;
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('loading');
    const error = document.getElementById('cart-errors');
    if (error) error.textContent = '';
    let added = false;
    let rejected = false;
    try {
      const formData = new FormData(form);
      const response = await fetch(routes.cart_add_url, {
        ...fetchConfig('javascript'),
        body: JSON.stringify({
          id: formData.get('id'),
          quantity: Number(formData.get('quantity') || 1),
          sections: cartNotification.getSectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        rejected = true;
        throw new Error(state.description || state.message || window.cartStrings.error);
      }
      added = true;
      cartNotification.renderContents(state, { fromUpsell: true, focus, product: form.dataset.productTitle });
    } catch (failure) {
      if (added || !rejected) {
        window.location.assign(document.getElementById('cart-icon-bubble').href);
      } else if (error) {
        error.setAttribute('role', 'alert');
        error.textContent = failure.message || window.cartStrings.error;
      }
    } finally {
      cartNotification.upsellAdding = false;
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('aria-busy');
      btn.textContent = originalText;
      btn.classList.remove('loading');
    }
  });
});
